'use client';

import type { OtherTimelineItem } from '@/lib/pr-v2/timeline-types';
import type { PrV2DiscussionResponse, PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { updatePrV2Title } from './fetch-pr-v2';
import { prV2DiscussionQueryKey, prV2SummaryQueryKey } from './query-keys';

import { patchInboxPullRequest } from '@/data/pull-requests/patch-inbox-pull-request';
import { clearPendingOptimisticMutation } from '@/data/pull-requests/pending-optimistic-mutations';
import {
  inboxV2MergedPullRequestsQueryKeyPrefix,
  inboxV2OpenPullRequestsQueryKeyPrefix,
} from '@/data/pull-requests/query-keys';
import { TIMELINE_ITEM_TYPE } from '@/lib/pr-v2/timeline-types';
import { asRecord } from '@/lib/pr-v2/utils/as-record';
import { useAuth } from '@/providers/auth-provider';

type MutationContext = {
  summaryKey: ReturnType<typeof prV2SummaryQueryKey>;
  discussionKey: ReturnType<typeof prV2DiscussionQueryKey>;
  optimisticTitle: string;
  previousTitle: string | null;
  previousGithubUpdatedAt: string | null;
  previousMetadataUpdatedAt: string | null;
  optimisticTimelineItemId?: string;
};

function withTouchedSummary(
  current: PrV2SummaryResponse | undefined,
  nextTitle: string,
  touchedAt: string,
): PrV2SummaryResponse | undefined {
  if (!current) {
    return current;
  }

  const metadataRecord = asRecord(current.pullRequest.metadata) ?? {};
  const pullRequestMetadata = asRecord(metadataRecord.pullRequest) ?? {};

  return {
    ...current,
    pullRequest: {
      ...current.pullRequest,
      title: nextTitle,
      githubUpdatedAt: touchedAt,
      metadataSummary: {
        ...current.pullRequest.metadataSummary,
        updatedAt: touchedAt,
      },
      metadata: {
        ...metadataRecord,
        pullRequest: {
          ...pullRequestMetadata,
          updated_at: touchedAt,
        },
      },
    },
  };
}

function buildOptimisticTimelineItem(args: {
  id: string;
  previousTitle: string | null;
  nextTitle: string;
  touchedAt: string;
  login: string;
  avatarUrl: string;
  htmlUrl: string;
}): OtherTimelineItem {
  return {
    id: args.id,
    type: TIMELINE_ITEM_TYPE.OtherTimelineItem,
    timestamp: args.touchedAt,
    user: {
      login: args.login,
      avatar_url: args.avatarUrl,
      html_url: args.htmlUrl,
    },
    event: 'renamed',
    rename: {
      from: args.previousTitle ?? '',
      to: args.nextTitle,
    },
    node_id: '',
    url: '',
    actor: {
      login: args.login,
      id: 0,
      node_id: '',
      avatar_url: args.avatarUrl,
      gravatar_id: null,
      url: args.htmlUrl,
      html_url: args.htmlUrl,
      followers_url: '',
      following_url: '',
      gists_url: '',
      starred_url: '',
      subscriptions_url: '',
      organizations_url: '',
      repos_url: '',
      events_url: '',
      received_events_url: '',
      type: 'User',
      site_admin: false,
      name: null,
      email: null,
    },
    commit_id: '',
    commit_url: '',
    created_at: args.touchedAt,
  } as OtherTimelineItem;
}

function appendOptimisticTimelineItem(
  current: PrV2DiscussionResponse | undefined,
  optimisticTimelineItem: OtherTimelineItem,
): PrV2DiscussionResponse | undefined {
  if (!current) {
    return current;
  }

  if (current.timelineItems.some((item) => item.id === optimisticTimelineItem.id)) {
    return current;
  }

  return {
    ...current,
    timelineItems: [...current.timelineItems, optimisticTimelineItem],
  };
}

function removeTimelineItem(
  current: PrV2DiscussionResponse | undefined,
  timelineItemId: string,
): PrV2DiscussionResponse | undefined {
  if (!current) {
    return current;
  }

  const nextTimelineItems = current.timelineItems.filter((item) => item.id !== timelineItemId);
  if (nextTimelineItems.length === current.timelineItems.length) {
    return current;
  }

  return {
    ...current,
    timelineItems: nextTimelineItems,
  };
}

/**
 * Mutates PR title with optimistic summary cache updates and rollback on failure.
 */
export function usePrV2UpdateTitle(owner: string, repo: string, number: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const summaryKey = prV2SummaryQueryKey(owner, repo, number);
  const discussionKey = prV2DiscussionQueryKey(owner, repo, number);

  return useMutation<{ title: string }, Error, string, MutationContext>({
    mutationFn: (title) => updatePrV2Title(owner, repo, number, title.trim()),
    onMutate: async (title) => {
      const normalizedTitle = title.trim();
      const touchedAt = new Date().toISOString();

      await Promise.all([
        queryClient.cancelQueries({ queryKey: summaryKey }),
        queryClient.cancelQueries({ queryKey: discussionKey }),
      ]);

      const previousSummary = queryClient.getQueryData<PrV2SummaryResponse>(summaryKey);
      const metadataRecord = previousSummary
        ? asRecord(previousSummary.pullRequest.metadata)
        : null;
      const pullRequestMetadata = metadataRecord ? asRecord(metadataRecord.pullRequest) : null;
      const previousMetadataUpdatedAt =
        previousSummary?.pullRequest.metadataSummary?.updatedAt ??
        (pullRequestMetadata && typeof pullRequestMetadata.updated_at === 'string'
          ? pullRequestMetadata.updated_at
          : null);
      const optimisticTimelineItemId = previousSummary?.pullRequest
        ? `optimistic-title-${Date.now()}`
        : undefined;

      // Optimistically update local summary title for immediate UI feedback.
      queryClient.setQueryData<PrV2SummaryResponse>(summaryKey, (current) =>
        withTouchedSummary(current, normalizedTitle, touchedAt),
      );

      if (optimisticTimelineItemId && previousSummary?.pullRequest) {
        const optimisticTimelineItem = buildOptimisticTimelineItem({
          id: optimisticTimelineItemId,
          previousTitle: previousSummary.pullRequest.title,
          nextTitle: normalizedTitle,
          touchedAt,
          login: user?.user_metadata?.user_name ?? '',
          avatarUrl: user?.user_metadata?.avatar_url ?? '',
          htmlUrl: user?.user_metadata?.html_url ?? '',
        });

        queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
          appendOptimisticTimelineItem(current, optimisticTimelineItem),
        );
      }

      patchInboxPullRequest(queryClient, { owner, repo, number: Number(number) }, (pr) => ({
        ...pr,
        title: normalizedTitle,
        updatedAt: touchedAt,
      }));

      return {
        summaryKey,
        discussionKey,
        optimisticTitle: normalizedTitle,
        previousTitle: previousSummary?.pullRequest.title ?? null,
        previousGithubUpdatedAt: previousSummary?.pullRequest.githubUpdatedAt ?? null,
        previousMetadataUpdatedAt,
        optimisticTimelineItemId,
      };
    },
    onError: (error, _title, context) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update title');
      // A failed mutation must drop the optimistic inbox lock; otherwise preserveOptimisticEntries
      // re-applies the stale snapshot over the recovery refetch for up to its 60s TTL.
      clearPendingOptimisticMutation({ owner, repo, number: Number(number) });

      if (!context) {
        return;
      }

      queryClient.setQueryData<PrV2SummaryResponse>(context.summaryKey, (current) => {
        if (!current || current.pullRequest.title !== context.optimisticTitle) {
          return current;
        }

        const metadataRecord = asRecord(current.pullRequest.metadata) ?? {};
        const pullRequestMetadata = asRecord(metadataRecord.pullRequest) ?? {};

        return {
          ...current,
          pullRequest: {
            ...current.pullRequest,
            title: context.previousTitle,
            githubUpdatedAt: context.previousGithubUpdatedAt,
            metadataSummary: {
              ...current.pullRequest.metadataSummary,
              updatedAt: context.previousMetadataUpdatedAt,
            },
            metadata: {
              ...metadataRecord,
              pullRequest: {
                ...pullRequestMetadata,
                updated_at: context.previousMetadataUpdatedAt,
              },
            },
          },
        };
      });

      if (context.optimisticTimelineItemId) {
        queryClient.setQueryData<PrV2DiscussionResponse>(context.discussionKey, (current) =>
          removeTimelineItem(current, context.optimisticTimelineItemId as string),
        );
      }
    },
    onSuccess: ({ title }, _variables, context) => {
      toast.success('Title updated');
      const targetSummaryKey = context?.summaryKey ?? summaryKey;
      const touchedAt = new Date().toISOString();
      queryClient.setQueryData<PrV2SummaryResponse>(targetSummaryKey, (current) => {
        return withTouchedSummary(current, title, touchedAt);
      });
    },
    onSettled: async (_data, _error, _variables, context) => {
      const targetSummaryKey = context?.summaryKey ?? summaryKey;
      const targetDiscussionKey = context?.discussionKey ?? discussionKey;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: targetSummaryKey }),
        queryClient.invalidateQueries({ queryKey: targetDiscussionKey }),
        queryClient.invalidateQueries({ queryKey: inboxV2OpenPullRequestsQueryKeyPrefix() }),
        queryClient.invalidateQueries({ queryKey: inboxV2MergedPullRequestsQueryKeyPrefix() }),
      ]);
    },
  });
}
